import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { lighten, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';

import type { SidebarItem } from './types';
import { useSidebarData } from './use-sidebar-data';

interface MobileNavDialogProps {
  open: boolean;
  onClose: () => void;
}

interface NavTileProps {
  item: SidebarItem;
  onClose: () => void;
}

const NavTile = ({ item, onClose }: NavTileProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isActive = item.path ? location.pathname === item.path : false;
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;

  const handleClick = () => {
    if (hasChildren && item.path) {
      navigate({ to: item.path });
    }
    if (item.onClick) {
      item.onClick();
    }
    onClose();
  };

  const tile = (
    <Box
      onClick={hasChildren || item.onClick ? handleClick : onClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
        py: 1.25,
        px: 2,
        borderRadius: 2,
        cursor: 'pointer',
        flex: 1,
        color: isActive ? 'primary.main' : theme.palette.grey[500],
        transition: 'color 0.15s ease',
        '&:active': {
          bgcolor: lighten(theme.palette.primary.main, 0.92),
        },
      }}
    >
      <Icon size={24} width="24px" height="24px" />
      <Typography variant="caption" fontWeight={400} fontSize="9px" lineHeight={1} sx={{ color: 'inherit' }}>
        {item.label}
      </Typography>
    </Box>
  );

  if (item.path && !hasChildren) {
    return (
      <Link to={item.path} style={{ textDecoration: 'none', color: 'inherit', flex: 1, display: 'flex' }}>
        {tile}
      </Link>
    );
  }

  return tile;
};

export const MobileNavDialog = ({ open, onClose }: MobileNavDialogProps) => {
  const { sidebarData } = useSidebarData();

  const allItems = sidebarData
    .flatMap(section => section.items)
    .filter(item => item.path || item.children?.length || item.onClick);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="bottom"
      PaperProps={{
        sx: {
          width: 'auto',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          px: 2,
          pt: 1,
          pb: 'env(safe-area-inset-bottom, 16px)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
        <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-around', pb: 1 }}>
        {allItems.map(item => (
          <NavTile key={item.id} item={item} onClose={onClose} />
        ))}
      </Box>
    </Drawer>
  );
};
